import { ReactNode } from "react";

interface SettingsGroupStateProps {
  isLoading: boolean;
  hasError: boolean;
  isEmpty: boolean;
  // What a failed load says. Fixed copy — the reason is in the console, not here.
  errorMessage: string;
  // What an empty card says, and what the reader can do about it.
  emptyMessage: string;
  emptyHint: ReactNode;
  // Placeholder rows drawn while loading; pick roughly what will replace them.
  skeletonRows?: number;
  children: ReactNode;
}

/**
 * What a settings card shows before its contents exist: loading, failed, or
 * empty. Every collection on a settings page answers those three here, so they
 * cannot drift apart in skeleton rhythm, voice, or spacing.
 *
 * Loading outranks empty: a card that has not loaded yet has not established
 * that it holds nothing, and saying so before the answer arrives is a claim the
 * data may contradict a moment later. An empty card always names its next step
 * — an empty state that only says "nothing here" leaves the reader to guess
 * whether that is a problem and whose it is.
 *
 * Contents outrank a failure. SWR keeps the last good response when a
 * revalidation fails, and these lists revalidate on window focus, so a failed
 * fetch routinely arrives alongside data that is still in hand and still
 * correct. The failure is only worth a message when it leaves nothing to show.
 */
export const SettingsGroupState = ({
  isLoading,
  hasError,
  isEmpty,
  errorMessage,
  emptyMessage,
  emptyHint,
  skeletonRows = 3,
  children,
}: SettingsGroupStateProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-4" role="status" aria-busy="true">
        <span className="sr-only">Loading…</span>
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <div key={i} className="h-11 animate-pulse rounded-lg bg-muted" aria-hidden="true" />
        ))}
      </div>
    );
  }

  if (isEmpty) {
    return hasError ? (
      <div className="px-4 py-10 text-center">
        <p className="text-sm text-destructive">{errorMessage}</p>
      </div>
    ) : (
      <div className="px-4 py-10 text-center">
        <p className="text-sm font-medium text-foreground">{emptyMessage}</p>
        <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">{emptyHint}</p>
      </div>
    );
  }

  return <>{children}</>;
};
